import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  cascaisPageImages, 
  lisbonPageImages, 
  sintraPageImages, 
  obidosPageImages, 
  tomarPageImages, 
  evoraPageImages, 
  nazarePageImages, 
  penichePageImages 
} from '../utils/imageLibrary';

// Map image keys to actual image arrays
const imageMap = {
  cascais: cascaisPageImages,
  lisbon: lisbonPageImages,
  sintra: sintraPageImages,
  obidos: obidosPageImages,
  tomar: tomarPageImages,
  evora: evoraPageImages,
  nazare: nazarePageImages,
  peniche: penichePageImages
};

export const LocationPage = ({ config }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const images = imageMap[config.imageKey] || [];
  
  // Preload first image
  useEffect(() => {
    if (images.length > 0) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = images[0].url;
      document.head.appendChild(link);
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [images]);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleBookingClick = () => {
    window.open('https://www.airbnb.com/rooms/1602726580833861863', '_blank', 'noopener,noreferrer');
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Full-Screen Hero Slideshow */}
      <section className="relative h-screen w-full overflow-hidden group">
        {/* Images */}
        {images.map((image, index) => (
          <div
            key={image.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <img 
              src={image.url}
              alt={image.title}
              className="h-full w-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              width={1920}
              height={1080}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent"></div>
          </div>
        ))}
        
        {/* Content Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-end pb-20 px-4">
          <div className="text-center max-w-4xl">
            <div className="inline-block px-4 py-1.5 rounded-full bg-background/20 backdrop-blur-md border border-background/30 text-background text-xs font-medium mb-4">
              {config.badge}
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-bold text-background mb-4">
              {images[currentSlide]?.title}
            </h1>
            <p className="text-xl sm:text-2xl text-background/95 mb-8">
              {images[currentSlide]?.subtitle}
            </p>
          </div>
          
          {/* Slide Indicators */}
          <div className="flex gap-2 mt-8">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  index === currentSlide 
                    ? 'w-8 bg-background' 
                    : 'w-1.5 bg-background/40 hover:bg-background/70'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Navigation Arrows */}
        <div className="absolute inset-0 z-30 flex items-center justify-between px-6 sm:px-10 pointer-events-none">
          <button
            onClick={prevSlide}
            className="pointer-events-auto p-3 rounded-full bg-background/90 backdrop-blur-md text-foreground hover:bg-background transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 shadow-lg"
          >
            <ChevronLeft size={24} strokeWidth={2.5} />
          </button>
          <button
            onClick={nextSlide}
            className="pointer-events-auto p-3 rounded-full bg-background/90 backdrop-blur-md text-foreground hover:bg-background transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 shadow-lg"
          >
            <ChevronRight size={24} strokeWidth={2.5} />
          </button>
        </div>
      </section>
      
      {/* Compact Info Section */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
              {config.content.title}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {config.content.subtitle}
            </p>
          </div>
          
          {/* Historical Narrative */}
          <div className="max-w-3xl mx-auto mb-12 space-y-4 text-center">
            {config.content.paragraphs.map((paragraph, index) => (
              <p key={index} className="text-base sm:text-lg text-foreground/90 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
          
          {/* Quick Facts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {config.content.stats.map((stat, index) => (
              <div key={index} className="p-8 rounded-tl-xl rounded-br-xl rounded-tr-none rounded-bl-none bg-card border border-border text-center hover:shadow-lg transition-all duration-300">
                <div className="text-5xl font-display font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground mb-2">{stat.label}</div>
                <div className="text-xs text-muted-foreground">{stat.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Large Photo Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {images.map((image, index) => (
              <div 
                key={image.id}
                className="relative overflow-hidden rounded-tl-xl rounded-br-xl rounded-tr-none rounded-bl-none aspect-[4/3] group cursor-pointer"
                onClick={() => setCurrentSlide(index)}
              >
                <img 
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                  loading="lazy"
                  width={800}
                  height={600}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="text-2xl font-display font-bold text-background mb-2">
                      {image.title}
                    </h3>
                    <p className="text-background/90">
                      {image.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Simple CTA */}
      <section className="py-20 px-4 bg-gradient-to-b from-muted/30 to-background">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
            {config.ctaTitle}
          </h2>
          <p className="text-xl text-muted-foreground mb-10 leading-relaxed">
            {config.ctaDescription}
          </p>
          <Button 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-warm px-10 py-6 text-lg"
            onClick={handleBookingClick}
          >
            Book Your Stay
          </Button>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
